<script setup lang="ts">
import type Address from '@/components/auth/register/AddressFields.vue';

type Props = {
  opacity?: boolean;
  allowGrow?: boolean;
  // rótulo alternativo do botão de cancelar edição (Minha Conta V2 usa "Voltar")
  cancelLabel?: string;
};

const {
  opacity = false,
  allowGrow = false,
  cancelLabel = '',
} = defineProps<Props>();

const emit = defineEmits(['check-pendency']);

const route = useRoute();
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { registerData } = storeToRefs(useRegisterStore());
const { isAddressComplete } = useUserValidations();
const { isModuleNeededOnFirstWithdraw } = useModulesValidation();

const {
  fetchUserProfile,
  userId,
  userAddressZipcode,
  userAddressFullAddress,
  userAddressCity,
  userAddressState,
  validateAddressByUser,
} = useUserData();

const addressFieldsRef = ref<InstanceType<typeof Address> | null>(null);

const isLoading = ref<boolean>(false);
const isEditing = ref<boolean>(false);

async function onEditAddressSubmit() {
  await $containment.handle('update_data', () => {
    sendForm();
  });
}

type UpdateAddressData = {
  zipcode: string;
  address: string;
  city: string;
  state: string;
  country: string;
  password_check?: string;
  kyc_id?: number;
};

async function sendForm() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  const sendData: UpdateAddressData = {
    zipcode: registerData.value.zipcode,
    address: registerData.value.address,
    city: registerData.value.city,
    state: registerData.value.state,
    country: registerData.value.country_data,
  };

  if ($kyc?.current?.id) {
    sendData.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    sendData.password_check = temporaryPassword.get();
  }

  if (
    !userId.value ||
    addressFieldsRef.value?.hasError ||
    !addressFieldsRef.value?.anyChanges
  ) {
    return;
  }

  isLoading.value = true;

  const { ApiService } = useApiService();

  await ApiService.patch('/bff/users/update-address', sendData)
    .then(async () => {
      openToaster({
        message: t('account.address_section.update_success'),
        type: 'success',
      });

      await fetchUserProfile({ force: true });

      isEditing.value = false;
      emit('check-pendency');
    })
    .catch((err) => {
      const errorData = err?.data?.message;

      const errorMessage =
        errorData || t('account.user_data.errors.update_error');

      openToaster({
        message: errorMessage,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

const hasPendency = computed(() => {
  return !isAddressComplete.value;
});

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

defineExpose({
  name: 'address',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  if (route.query?.highlight === 'address' && hasPendency.value) {
    isEditing.value = true;
  }
});

const isLoadingCheckAddress = ref<boolean>(false);

onMounted(async () => {
  if (
    !userAddressZipcode.value ||
    !userAddressFullAddress.value ||
    !userAddressCity.value ||
    !userAddressState.value
  ) {
    isLoadingCheckAddress.value = true;
    try {
      await validateAddressByUser();
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingCheckAddress.value = false;
    }
  }
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      :class="[
        $style.container,
        { [$style.opacity]: opacity, [$style.grow]: allowGrow && !isEditing },
      ]"
    >
      <BaseLoading
        class="!bg-auth-bg-primary !absolute"
        :icon-loader="true"
        :show="isLoadingCheckAddress"
        size="small"
      />

      <UserProfileDataCard
        :action-label="
          isEditing
            ? cancelLabel || $t('general.cancel')
            : isAddressComplete
              ? $t('general.edit')
              : undefined
        "
        :action-type="!isEditing && isAddressComplete ? 'edit' : undefined"
        :badge-label="
          isAddressComplete ? $t('account.complete') : $t('account.incomplete')
        "
        :badge-type="isAddressComplete ? 'success' : 'error'"
        :label="$t('account.address_section.address')"
        :required="isModuleNeededOnFirstWithdraw('address')"
        @action="toggleEdit"
      >
        <template v-if="isAddressComplete">
          {{
            `${userAddressFullAddress}, ${userAddressCity}/${userAddressState}, ${userAddressZipcode}`
          }}
        </template>
        <!-- CTA "clique aqui" só faz sentido com o editor fechado -->
        <span
          v-else-if="!isEditing"
          :class="$style.incompleteBtn"
          @click="toggleEdit"
        >
          {{ $t('account.address_section.incomplete_address') }}
        </span>
        <template v-else>
          {{ $t('account.incomplete') }}
        </template>
      </UserProfileDataCard>

      <form
        v-if="isEditing"
        :class="$style.editContent"
        @submit.prevent="onEditAddressSubmit"
      >
        <div :class="$style.description">
          {{ $t('account.address_section.description') }}
        </div>

        <div :class="[$style.formGroup, 'registerFields']">
          <AuthRegisterAddressFields
            ref="addressFieldsRef"
            get-values-from-user
          />
        </div>

        <div class="flex justify-between items-end w-full col-span-2">
          <button
            :class="$style.btnSubmit"
            :disabled="
              !addressFieldsRef?.anyChanges || addressFieldsRef?.hasError
            "
            type="submit"
          >
            <NuxtIcon name="check" />
            {{ $t('account.save') }}
          </button>
        </div>
      </form>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-3 relative;
}

.grow {
  --card-grow: 1;
}

.opacity {
  @apply opacity-20 pointer-events-none select-none;
}

.incompleteBtn {
  @apply text-primary cursor-pointer;
}

.editContent {
  @apply w-full;

  .description {
    @apply text-sm text-texts/70 mb-4 max-w-full;
  }

  .formGroup {
    @apply mb-4 relative max-w-full p-0;

    label {
      @apply text-xs font-medium;
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }
}
</style>
